import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loansApi } from '../../api/endpoints/loans';
import {
  LoanApplicationRequest,
  ApplicationApprovalRequest,
  ApplicationRejectionRequest,
  LoanQueryParams,
  ApplicationQueryParams,
  LoanCalculationRequest,
  LoanProductRequest,
} from '../../api/types/loan.types';

// Products
export const useLoanProducts = () => {
  return useQuery({
    queryKey: ['loanProducts'],
    queryFn: () => loansApi.listProducts(),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useLoanProduct = (productId: string) => {
  return useQuery({
    queryKey: ['loanProduct', productId],
    queryFn: () => loansApi.getProduct(productId),
    enabled: !!productId,
  });
};

export const useCreateLoanProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LoanProductRequest) => loansApi.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanProducts'] });
    },
  });
};

export const useUpdateLoanProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: LoanProductRequest }) =>
      loansApi.updateProduct(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanProducts'] });
    },
  });
};

export const useDeleteLoanProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => loansApi.deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanProducts'] });
    },
  });
};

// Applications
export const useLoanApplications = (params?: ApplicationQueryParams) => {
  return useQuery({
    queryKey: ['loanApplications', params],
    queryFn: () => loansApi.listApplications(params),
  });
};

export const useCalculateLoan = () => {
  return useMutation({
    mutationFn: (data: LoanCalculationRequest) => loansApi.calculateLoan(data),
  });
};

export const useLoanApplication = (applicationId: string) => {
  return useQuery({
    queryKey: ['loanApplication', applicationId],
    queryFn: () => loansApi.getApplication(applicationId),
    enabled: !!applicationId,
  });
};

export const useSubmitApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LoanApplicationRequest) => loansApi.submitApplication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] });
    },
  });
};

export const useApproveApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }: { applicationId: string; data: ApplicationApprovalRequest }) =>
      loansApi.approveApplication(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] });
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
};

export const useRejectApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }: { applicationId: string; data: ApplicationRejectionRequest }) =>
      loansApi.rejectApplication(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] });
    },
  });
};

export const useRetriggerFeePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => loansApi.retriggerFeePayment(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] });
    },
  });
};

// Loans
export const useLoans = (params?: LoanQueryParams) => {
  return useQuery({
    queryKey: ['loans', params],
    queryFn: () => loansApi.list(params),
  });
};

export const useLoan = (loanId: string) => {
  return useQuery({
    queryKey: ['loan', loanId],
    queryFn: () => loansApi.getById(loanId),
    enabled: !!loanId,
  });
};

export const useLoanSchedule = (loanId: string) => {
  return useQuery({
    queryKey: ['loanSchedule', loanId],
    queryFn: () => loansApi.getSchedule(loanId),
    enabled: !!loanId,
  });
};

export const useLoanTransactions = (loanId: string) => {
  return useQuery({
    queryKey: ['loanTransactions', loanId],
    queryFn: () => loansApi.getTransactions(loanId),
    enabled: !!loanId,
  });
};
